import { QueryClient } from '@tanstack/react-query';
import {
  AgentHandoff,
  HANDOFF_AGENT_IDS,
  HANDOFF_ROSTER,
  HandoffAgentId
} from '@blisspointmedia/bpm-types/dist/agents';
import { ChartConfig, DashboardChart } from '@blisspointmedia/bpm-types/dist/dashboarding';
import { FilterNode, isScpQuery, QueryConfig } from '@blisspointmedia/bpm-types/dist/querying';
import { HANDOFF_TOOL_NAME } from '@Components/AgentChat/agent-chat-constants';
import { AgentToolCallSegment, AgentResponseSegment } from '../../utils/agent-client';
import { Agent } from '../../utils/agent-endpoints';
import { ChartWithData, chartDataKeyPrefix, chartEnvelopeKey } from '../Charts/hooks/useChartData';
import { ChartHandle } from '../Charts/chart-handle';
import { appliedDashboardFilterNodes } from '../Dashboard/dashboard-filter-utils';

export const SCOPE_GUARD_NOTE = "Analysis isn't available for charts using a legacy filter preset.";
export const HANDOFF_FAILED_NOTE = "Couldn't hand this off to another agent.";

export const isHandoffAgentId = (value: unknown): value is HandoffAgentId =>
  HANDOFF_AGENT_IDS.some(id => id === value);

const displayName = (id: HandoffAgentId): string =>
  HANDOFF_ROSTER.find(entry => entry.id === id)?.displayName ?? id;

export const handedToNote = (target: HandoffAgentId): string => `Handed to ${displayName(target)}`;

export const declinedHandoffNote = (from: HandoffAgentId, target: HandoffAgentId): string =>
  `${displayName(from)} suggested handing this to the ${displayName(target)} agent. Rephrase to ` +
  `continue with ${displayName(from)}, or ask again to switch.`;

export const messageLabelFor = (agent: string | undefined): string | undefined =>
  agent === Agent.DATA_ANALYSIS ? `Answered by ${displayName(agent)}` : undefined;

// A chartId the model picked; the tool only offers handles of the focused charts.
const isChartHandle = (value: unknown): value is ChartHandle =>
  typeof value === 'string' && (value.startsWith('chart:') || value.startsWith('draft:'));

export const isHandoffCall = (segment: AgentResponseSegment): segment is AgentToolCallSegment =>
  segment.type === 'tool_call' && segment.name === HANDOFF_TOOL_NAME;

export const QUERY_DATA_TOOL_NAME = 'query_data';

export const findHandoffCall = (
  segments: AgentResponseSegment[]
): AgentToolCallSegment | undefined => segments.find(isHandoffCall);

// The query half of a chart: what a receiving agent needs, without encoding, controls or identity.
export const toQueryConfig = ({ queries, merge, comparison }: QueryConfig): QueryConfig => ({
  queries,
  ...(merge && { merge }),
  ...(comparison && { comparison })
});

// Any data model is in scope except a legacy `filterPreset`: agents are never taught presets, so the
// analysis agent couldn't explain or adjust a handed config carrying one.
export const isAnalysisInScope = ({ queries }: QueryConfig): boolean =>
  queries.length > 0 && queries.every(query => !(isScpQuery(query) && query.filterPreset));

// The cached render the user is looking at (config sent to /getData + response). Recency alone can
// name a selection they left, so an entry a mounted chart subscribes to wins, else the newest.
export const executedChartData = (
  queryClient: QueryClient,
  company: string,
  chartId: ChartHandle
): ChartWithData | undefined => {
  const queryKey = chartDataKeyPrefix(company, chartId);
  const newestEntry = (type: 'active' | 'all') =>
    queryClient
      .getQueryCache()
      .findAll({ queryKey, type })
      .map(query => ({
        entry: queryClient.getQueryData<ChartWithData>(query.queryKey),
        updatedAt: query.state.dataUpdatedAt
      }))
      .filter(({ entry }) => entry?.config)
      .sort((a, b) => b.updatedAt - a.updatedAt)[0]?.entry;
  return newestEntry('active') ?? newestEntry('all');
};

const savedChartConfig = (
  queryClient: QueryClient,
  company: string,
  chartId: ChartHandle
): ChartConfig | undefined =>
  queryClient.getQueryData<DashboardChart>(chartEnvelopeKey(company, chartId))?.config;

export interface ResolvedChartConfigs {
  executed?: QueryConfig;
  saved?: QueryConfig;
  // The dashboard filters applied to the executed render, kept apart from its config.
  dashboardFilters?: FilterNode[];
}

export const resolveChartConfigs = (
  queryClient: QueryClient,
  company: string,
  chartId: ChartHandle
): ResolvedChartConfigs => {
  const executed = executedChartData(queryClient, company, chartId);
  const saved = savedChartConfig(queryClient, company, chartId);
  const dashboardFilters = executed ? appliedDashboardFilterNodes(executed) : [];
  return {
    ...(executed && { executed: toQueryConfig(executed.config) }),
    ...(saved && { saved: toQueryConfig(saved) }),
    ...(dashboardFilters.length > 0 && { dashboardFilters })
  };
};

// The QueryConfig the data analysis agent successfully ran for its answer: from its last
// `query_data` call whose result succeeded. The result's `executed_config` is exact; for
// `use_handoff_config` it is the config this run was handed. A call with no result, or a failed one,
// proves nothing, so none of its args are used.
export const executedAnalysisConfig = (
  segments: AgentResponseSegment[],
  sentHandoff?: AgentHandoff
): QueryConfig | undefined =>
  segments
    .filter(
      (s): s is AgentToolCallSegment =>
        s.type === 'tool_call' && s.name === QUERY_DATA_TOOL_NAME && s.resultOk === true
    )
    .map(
      call =>
        call.executedConfig ??
        (call.args.use_handoff_config === true ? sentHandoff?.queryConfig : undefined)
    )
    .filter(config => config)
    .pop();

// The config of the successful `query_data` result with that `result_id`, from the current run or
// earlier persisted data analysis messages. Messages saved before `result_id` existed never match.
export const analysisResultConfig = (
  segments: AgentResponseSegment[],
  resultId: string
): QueryConfig | undefined =>
  segments.find(
    (s): s is AgentToolCallSegment =>
      s.type === 'tool_call' &&
      s.name === QUERY_DATA_TOOL_NAME &&
      s.resultOk === true &&
      s.resultId === resultId
  )?.executedConfig;

export interface HandoffPlan {
  note: string;
  target?: HandoffAgentId;
  handoff?: AgentHandoff;
}

interface PlanHandoffArgs {
  args: Record<string, unknown>;
  from: HandoffAgentId;
  // At most one executed handoff per user turn: a follow-up run that hands off again is not run.
  isFollowUp: boolean;
  resolveChart: (chartId: ChartHandle) => ResolvedChartConfigs;
  // Config of the data analysis result a `resultId` names (see analysisResultConfig).
  resolveResult: (resultId: string) => QueryConfig | undefined;
  // Fallback when data analysis names no resultId: what its run last executed (see executedAnalysisConfig).
  executedConfig?: QueryConfig;
}

// Turns a `handoff` tool call into what to show and what (if anything) to run next. The model only
// names an artifact; the frontend attaches the verified config behind it, or none:
// - chartId: the chart's executed config, else its saved config (cached only), else none, plus the
//   dashboard filters applied to its executed render, if any.
// - resultId: the config that data analysis result executed; an unknown id gives none.
// - neither, from data analysis: the config its current run last executed, else none.
export const planHandoff = ({
  args,
  from,
  isFollowUp,
  resolveChart,
  resolveResult,
  executedConfig
}: PlanHandoffArgs): HandoffPlan => {
  const { target } = args;
  if (!isHandoffAgentId(target) || target === from) return { note: HANDOFF_FAILED_NOTE };
  if (isFollowUp) return { note: declinedHandoffNote(from, target) };

  const base = { from, question: typeof args.question === 'string' ? args.question : '' };
  const note = handedToNote(target);

  if (isChartHandle(args.chartId)) {
    const { chartId } = args;
    const { executed, saved, dashboardFilters } = resolveChart(chartId);
    // Checked against what's on screen, else the saved row, so a chart that hasn't rendered yet
    // can't slip a filter past the guard.
    const queryConfig = executed ?? saved;
    if (target === Agent.DATA_ANALYSIS && queryConfig && !isAnalysisInScope(queryConfig)) {
      return { note: SCOPE_GUARD_NOTE };
    }
    return {
      note,
      target,
      handoff: {
        ...base,
        chartId,
        ...(queryConfig && { queryConfig }),
        ...(target === Agent.DATA_ANALYSIS && dashboardFilters?.length && { dashboardFilters })
      }
    };
  }

  const queryConfig =
    typeof args.resultId === 'string' ? resolveResult(args.resultId) : executedConfig;
  return { note, target, handoff: { ...base, ...(queryConfig && { queryConfig }) } };
};
